import clsx from "clsx";
import { FC } from "react";

import { HoverableToolbar } from "components/hover-widgets/HoverableToolbar";
import { HideUnlessEditModeButton } from "components/materials/HideUnlessEditModeButton";
import { ActivityCard } from "components/materials/topic/activity/detail/ActivityCard";
import { DurationDisplayBox } from "components/materials/topic/activity/detail/DurationDisplayBox";
import { ActivityCategory } from "components/server-types";
import { t } from "i18n/i18n";
import { Card } from "mds/components/Card";
import { useCapabilities } from "store/capability-hooks";
import { useAppSelector } from "store/index";
import { selectCurrentSectionId, selectIsEditModeEnabled } from "store/selectors";
import { selectSortedActivitiesByTopicIdAndCategory } from "store/selectors/topics";
import { useListSelector } from "store/store-hooks";
import { useActivityActions } from "utils/activity";
import { useDnDItemOrdering, useLocalCopy } from "utils/collections";
import { sumActivityTimes } from "utils/page";

export type LessonActivityListProps = {
  topicId: string;
};

// This component is for displaying a list of lesson activities related to a topic.
export const LessonActivityList: FC<LessonActivityListProps> = ({ topicId }) => {
  const { canAuthorSection } = useCapabilities();
  const { createActivity } = useActivityActions();
  const currentSectionId = useAppSelector(selectCurrentSectionId);
  const isEditModeEnabled = useAppSelector(selectIsEditModeEnabled);
  const lessonActivitiesFromStore = useListSelector(
    (s) => selectSortedActivitiesByTopicIdAndCategory(s, topicId, ActivityCategory.LESSON),
    ["id", "category", "order", "title", "estimated_time", "topic_id"],
  );

  const [lessonActivities, setLessonActivities] = useLocalCopy(lessonActivitiesFromStore);
  const moveLessonActivityLocally = useDnDItemOrdering(
    lessonActivities,
    setLessonActivities,
    "category",
  );

  const totalLessonTime = sumActivityTimes(lessonActivities);
  const hasTotalLessonTime = canAuthorSection && Number(totalLessonTime) > 0;

  const createNewActivity = () => {
    if (!currentSectionId) return;
    createActivity(ActivityCategory.LESSON, topicId, currentSectionId);
  };

  return (
    // TODO: This DragAndDrop -> ListItem -> HoverableToolbar -> Icon+EditableText needs
    // to be abstracted so we don't have style drift between all the activity card types,
    // and other list types (like TopicList, CoursePageList, etc). We should instead express
    // any style differences as part of the ListItem MDS styles using the `kind` property.
    <>
      <div className="flex items-center">
        <div className="flex-auto">
          <HoverableToolbar readOnly={!canAuthorSection} reference="lesson" disableUnlessEditMode>
            <div
              className={clsx("body-s my-1 w-full truncate px-2 text-black-tint-40", {
                "cursor-pointer": isEditModeEnabled,
              })}
            >
              {t("glossary.lesson")}
            </div>
          </HoverableToolbar>
        </div>

        {hasTotalLessonTime && <DurationDisplayBox duration={totalLessonTime} />}
      </div>

      {lessonActivities.map((activity, activityIndex) => (
        <ActivityCard
          activity={activity}
          activityIndex={activityIndex}
          key={activity.id}
          moveActivityLocally={moveLessonActivityLocally}
        />
      ))}

      {isEditModeEnabled && (
        <Card small>
          <div className="-ml-1">
            <HideUnlessEditModeButton reference="activity" onClick={createNewActivity} />
          </div>
        </Card>
      )}
    </>
  );
};
